import { defineStore } from "pinia";
import { isEmpty } from "lodash";
import { format } from "../libs/date.format";
import { post } from "@/libs/axios.js";
import {
    ACTIVE_FEATURES,
    getFireBaseFeatureValue,
} from "../components/methods/getFeatureValue";
import { usePricingStore } from "./pricingStore";
import { useBookingSettingsStore } from "./bookingStore";

// eslint-disable-next-line import/prefer-default-export
export const useMainStore = defineStore(
    "main",
    {
        state: () => ({
            // mpv2
            isAdmin: false,
            showDeleteStatus: false,
            userType: "",
            IMAGE_BASE_LINK: `${window.imgUrl}${window.businessId}/`,
            defaultEmailForBusiness: "",
            businessId: null,
            businessName: "",
            businessSlug: "",
            businessLogo: "",
            allVenues: {},
            allActivities: {},
            actVenues: [],
            actTermdates: [],
            termDates: [],
            sessions: [],
            agesForActivities: {},
            actCategories: {},
            categories: {},
            holidays: {},
            advancedTermDates: [],
            termdateVenuesRel: [],
            termdateVenues: [],
            allCategories: [],
            simpleVenues: [],
            serviceVenues: [],
            serviceActivities: [],
            serviceActVenues: [],
            servicePricing: [],
            addNewEventSubmitted: false,
            addNewEventForSimpleFlowSubmitted: false,
            duplicateTermSubmitted: false,
            saveAndPublishSubmitted: false,
            updateAdvancedTermSubmitted: false,
            initialDataRetrieved: false,
            showDuplicateModal: false,
            showConfirmDeleteModal: false,
            showConfirmDeleteVenueModal: false,
            duplicateTermId: null,
            reSetCourse: false,
        }),

        getters: {

            isTermdateHaveVenueAndSessions(state) {
                return (termdateId) => {
                    const termDateVenuesIds = state.termdateVenuesRel.filter(
                        (tdv) => tdv.termdate_id === termdateId,
                    ).map((el) => el.venues_id);

                    const sessions = state.sessions.filter(
                        (s) => (
                            termDateVenuesIds.includes(s.venue_id)
                            && s.termdate_id === termdateId
                        ),
                    );

                    return sessions.length > 0;
                };
            },
        },

        actions: {
            capitalize(s) {
                return s && s[0].toUpperCase() + s.slice(1);
            },

            isEmpty(value) {
                return isEmpty(value);
            },

            // MP v2
            async getData() {
                const res = await post("/get-register-management-data");

                const { data } = res.data;

                if (res.data.success) {
                    this.businessId = data.businessId;
                    this.allVenues = data.venues;
                    this.allActivities = data.activities;
                    this.actVenues = data.activityVenues;
                    this.actTermdates = data.actTermdates;
                    this.termDates = data.termdates;
                    this.sessions = data.sessions;
                    this.simpleActivitiesIds = data.activities.reduce(
                        (idList, activity) => {
                            if (activity.flow_type === "simple") {
                                return [...idList, activity.activity_id];
                            }

                            return idList;
                        },
                        [],
                    );
                    this.agesForActivities = data.agesForActivities;
                    this.actCategories = data.actCategories;
                    this.categories = data.categories;
                    this.allCategories = data.allCategories;
                    this.holidays = data.holidays;
                    this.advancedTermDates = data.advancedTermDates;
                    this.termdateVenuesRel = data.termdateVenuesRel;
                    this.termdateVenues = data.termdateVenues;
                    this.defaultEmailForBusiness = data.defaultEmail;
                    this.businessName = data.businessName;
                    this.businessLogo = this.IMAGE_BASE_LINK + data.businessLogo;
                    this.businessSlug = data.businessSlug;
                    this.initialDataRetrieved = true;
                    this.isAdmin = data.isAdmin;
                    this.showDeleteStatus = data.showDelete;
                    this.userType = data.userType;
                    this.simpleVenues = data.simpleVenues;
                    this.serviceVenues = data.serviceVenues;
                    this.serviceActivities = data.serviceAct;
                    this.serviceActVenues = data.serviceActVenues;
                    this.servicePricing = data.servicePricing;
                    // eslint-disable-next-line no-extra-boolean-cast
                } else if (!!res.data.errors?.csrf) {
                    window.location.reload();
                }
            },

            async addNewEvent(data, termdateId, venueId, repetition) {
                const formattedDates = {
                    start: format(data.dates.start, "yyyy-mm-d"),
                    end: format(data.dates.end, "yyyy-mm-d"),
                };

                // eslint-disable-next-line no-param-reassign
                data.dates.formattedDates = formattedDates;

                if (!this.addNewEventSubmitted) {
                    this.addNewEventSubmitted = true;

                    const requestData = {
                        activity: data.activity,
                        dates: data.dates,
                        pricing: data.pricing,
                        times: data.times,
                        preselectedActivity: data.preselectedEvent,
                        termdateId,
                        venueId,
                        repetition,
                    };

                    const sellingTicketFeature = await getFireBaseFeatureValue(
                        ACTIVE_FEATURES.SELLING_TICKET,
                    );

                    if (sellingTicketFeature) {
                        const pricingStore = usePricingStore();
                        requestData.pricing = pricingStore.getOldPricingFormat;
                    }

                    const res = await post("/add-new-activity", requestData);

                    if (res.data.success) {
                        const timetableId = res.data.data.timetables.id;
                        const savingPricingAndBookingSettings = await
                        this.savePricingAndBookingSettings({
                            timetableId,
                            activityId: res.data.data.timetables.activity_id,
                            termdateId: res.data.data.termdateId,
                            venueId,
                        });
                        if (savingPricingAndBookingSettings === false) {
                            console.error("Saving pricing and booking settings data Failed");
                            return;
                        }
                    } else {
                        alert("Saving Data Failed");
                        console.log(res.data.errors);
                    }
                    this.addNewEventSubmitted = false;
                    await this.getData();
                }
            },

            async addNewEventForSimpleFlow(data) {
                if (!this.addNewEventForSimpleFlowSubmitted) {
                    this.addNewEventForSimpleFlowSubmitted = true;

                    const requestData = {
                        activity: data.eventDetails,
                        dates: data.eventDates,
                        pricing: data.eventPricing,
                        holidays: data.eventHolidays,
                        venueId: data.venueId,
                    };

                    const sellingTicketFeature = await getFireBaseFeatureValue(
                        ACTIVE_FEATURES.SELLING_TICKET,
                    );

                    if (sellingTicketFeature) {
                        const pricingStore = usePricingStore();
                        requestData.pricing = pricingStore.getOldPricingFormat;
                    }

                    const res = await post("/add-new-simple-event", requestData);

                    if (res.data.success) {
                        const timetableId = res.data.data.timetables.id;
                        const savingPricingAndBookingSettings = await
                        this.savePricingAndBookingSettings({
                            timetableId,
                            activityId: res.data.data.timetables.activity_id,
                            termdateId: res.data.data.termdateId,
                            venueId: data.venueId,
                        });
                        if (savingPricingAndBookingSettings === false) {
                            console.error("Saving pricing and booking settings data Failed");
                            return;
                        }
                    } else {
                        alert("Saving Data Failed");
                        console.log(res.data.errors);
                    }
                    this.addNewEventForSimpleFlowSubmitted = false;
                    this.getData();
                }
            },

            async submitDuplicateModal(data) {
                if (!this.duplicateTermSubmitted) {
                    this.duplicateTermSubmitted = true;

                    const res = await post("/duplicate-term", {
                        data,
                        termdateId: this.duplicateTermId,
                    });

                    await this.getData();
                    this.duplicateTermSubmitted = false;

                    const termdateID = res?.data?.data?.termdateId || null;
                    return termdateID;
                }

                return null;
            },

            async saveAndPublish(termdateId) {
                const formData = new FormData();

                formData.append("termdateId", termdateId);
                if (!this.saveAndPublishSubmitted) {
                    this.saveAndPublishSubmitted = true;

                    const res = await post("/save-and-publish-term", { termdateId });

                    this.saveAndPublishSubmitted = false;
                    if (res.data.success) {
                        await this.getData();
                        return true;
                    }

                    alert("Saving Data Failed");

                    return true;
                }
                return false;
            },

            async updateAdvancedTerm(data) {
                if (!this.updateAdvancedTermSubmitted) {
                    this.updateAdvancedTermSubmitted = true;

                    const res = await post("/update-advanced-term", data);

                    this.updateAdvancedTermSubmitted = false;
                    if (res.data.success) {
                        await this.getData();
                        return true;
                    }

                    // Instead of outputting the error and show general error,
                    // we need to expect the error and handle it appropriately
                    // by showing the user a message
                    if ("csrf" in res.data.errors) {
                        alert("Updating Term Failed. Your session has expired. Please refresh the page.");
                        return true;
                    }

                    if ("error" in res.data.errors) {
                        alert(`Updating Term Failed. ${res.data.errors.error}`);
                        return true;
                    }

                    console.log(res.data.errors);
                    alert("Unable to save term. Please try again later.");
                    return true;
                }

                return false;
            },

            async deleteAdvancedTerm(termdateId) {
                const res = await post("/delete-advanced-term", { termdateId });

                if (res.data.success) {
                    await this.getData();
                    return true;
                }
                // notifyAll(res.data.errors);

                alert("Deleting Term Failed");
                console.log(res.data.errors);
                return true;
            },

            async updateVenue(venue) {
                const res = await post("/update-venue", { venue });

                if (res.data.success) {
                    await this.getData();
                } else {
                    alert("Updating Venue Failed");
                    console.log(res.data.errors);
                }
            },

            async updateAdvancedVenueAddToSimple(venue, csrf) {
                const res = await post("/management-panel/simple/update-adv-venue-add-to-simple", {
                    venue,
                    csrf,
                });

                if (res.data.success) {
                    await this.getData();
                } else {
                    alert("Updating Venue Failed");
                    console.log(res.data.errors);
                }
            },

            async deleteVenue(venueId, activityIds) {
                const res = await post("/remove-venue", {
                    venueId,
                    activityIds,
                });

                if (res.data.success) {
                    await this.getData();
                }

                return res;
            },

            async deleteAdvancedVenue(venueId, activityIds, termdateId) {
                // eslint-disable-next-line no-unused-vars
                const res = await post("/remove-venue", {
                    venueId,
                    activityIds,
                    termdateId,
                });
                await this.getData();
            },

            // Event series simple flow
            async submitSimpleEventDetails(data) {
                return post("/management-panel/update-details", { activity: data });
            },

            async submitSimpleEventPrice(data) {
                return post("/management-panel/update-price", { price: data });
            },

            async submitSimpleEventDates(data) {
                return post("/management-panel/update-dates", { dates: data });
            },

            async submitAdvancedSessions(data) {
                return post("/management-panel/update-advanced-sessions", { data });
            },

            async submitSimpleEventDelete(data) {
                return post("/management-panel/remove-event", { ...data });
            },

            async submitSimpleEventUpdateHolidays(data) {
                return post("/management-panel/update-holidays", data);
            },

            async submitSimpleEventRemoveSingleHoliday(data) {
                return post("/management-panel/add-single-holiday", { holiday: data });
            },

            async submitSimpleEventRevertRemoveSingleHoliday(data) {
                return post("/management-panel/remove-single-holiday", { holiday: data });
            },

            async updateStatus(activityId, status) {
                return post("/update-status", { activityId, status });
            },

            async removeActivity(activityId) {
                return post("/remove-activity", { activityId });
            },

            async removeService(venueId, activityId) {
                return post("/remove-service", { activityId, venueId });
            },

            async updateServiceVenue(venueId, venueData) {
                return post("/update-service-venue", { venueId, venueData });
            },

            async updateServiceAges(ages, activityId) {
                return post("/update-service-ages", { ages, activityId });
            },

            // eslint-disable-next-line consistent-return
            async savePricingAndBookingSettings({
                timetableId, activityId, termdateId, venueId,
            }) {
                const sellingTicketFeature = await getFireBaseFeatureValue(
                    ACTIVE_FEATURES.SELLING_TICKET,
                );
                if (sellingTicketFeature) {
                    const pricingStore = usePricingStore();
                    const bookingStore = useBookingSettingsStore();
                    try {
                        await pricingStore.saveEventPricing(timetableId);
                    } catch (e) {
                        this.editedPrice = {
                            timetableId: "1",
                            activityId,
                            termdateId,
                            venueId,
                            freePricing: false,
                            freeTrial: "no",
                            price: "0",
                            pricePerTerm: "",
                            priceType: "per-month",
                            errors: "",
                        };
                        this.submitSimpleEventPrice(this.editedPrice);
                        console.error("Saving pricing data Failed", e);
                        alert("Saving pricing Failed");
                        return false;
                    }
                    if (pricingStore.sellingTicket) {
                        try {
                            await bookingStore.saveBookingSettings();
                        } catch (e) {
                            console.error("Saving booking settings data Failed", e);
                            alert("Saving booking settings data Failed");
                            return false;
                        }
                    }

                    return true;
                }
            },
        },
    },
);
